import type { RefObject } from "react";
import { useEffect, useMemo, useState } from "react";

import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";

import type { ColumnsType, ColumnType } from "@similarweb/pro-logic-components";

import {
    HIGHLIGHT_ANIMATION_DELAY,
    HIGHLIGHT_ANIMATION_REMOVE_DELAY,
    HIGHLIGHT_ANIMATION_TIME,
} from "app/pages/sales-intelligence/constants/highlight-animation";
import useSetTimeout from "app/pages/sales-intelligence/hooks/useSetTimeout";
import type { LeadsSearchRowWithKey } from "app/pages/sales-intelligence/modules/leads-search";
import {
    selectColumnNamesForHighlight,
    setColumnNamesForHighlight,
} from "app/pages/sales-intelligence/modules/leads-search";

const useHighlightColumns = <R extends LeadsSearchRowWithKey>(
    tableWrapperRef: RefObject<HTMLDivElement | null>,
    columns: ColumnsType<R>,
    isLoading: boolean,
): ColumnsType<R> => {
    const dispatch = useDispatch();
    const columnNamesForHighlight = useSelector(selectColumnNamesForHighlight);

    const [isHighlight, setIsHighlight] = useState<boolean>(false);

    const timeout = useSetTimeout();

    const scrollToColumnName = (): void => {
        if (!tableWrapperRef.current || columnNamesForHighlight.length === 0) {
            return;
        }

        const lastColumnName = columnNamesForHighlight[columnNamesForHighlight.length - 1];

        const tableHead = tableWrapperRef.current.querySelector(".ant-table-thead");
        const scrollTarget =
            tableWrapperRef.current.querySelector<HTMLElement>(".ant-table-body") ??
            tableWrapperRef.current.querySelector<HTMLElement>(".ant-table-header");

        if (!scrollTarget || !tableHead) {
            return;
        }

        const firstHeadRow = tableHead.querySelector("tr");

        if (!firstHeadRow) {
            return;
        }

        const allHeadCells: NodeListOf<HTMLTableCellElement> = firstHeadRow.querySelectorAll("th");
        const targetHeadCell: HTMLTableCellElement | null = firstHeadRow.querySelector(
            `[data-automation-field-header="${lastColumnName}"]`,
        );

        const targetCell = targetHeadCell || allHeadCells[allHeadCells.length - 1];

        if (!targetCell) {
            return;
        }

        const deltaX = targetCell.offsetLeft - scrollTarget.scrollLeft;

        if (deltaX === 0) {
            return;
        }

        scrollTarget.dispatchEvent(new WheelEvent("wheel", { deltaX, deltaY: 0, bubbles: true, cancelable: true }));
    };

    const executeColumnAnimation = (): void => {
        setIsHighlight(true);

        timeout(() => {
            setIsHighlight(false);

            timeout(() => {
                dispatch(setColumnNamesForHighlight([]));
            }, HIGHLIGHT_ANIMATION_TIME);
        }, HIGHLIGHT_ANIMATION_REMOVE_DELAY);
    };

    useEffect(() => {
        if (!columns.length || isLoading || !columnNamesForHighlight.length) {
            return;
        }

        timeout(() => {
            scrollToColumnName();
            executeColumnAnimation();
        }, HIGHLIGHT_ANIMATION_DELAY);
    }, [columns, isLoading]);

    return useMemo(() => {
        if (!columnNamesForHighlight.length) {
            return columns;
        }

        const columnNamesSet = new Set(columnNamesForHighlight);

        return columns.map((col): ColumnType<R> => {
            if (!columnNamesSet.has(String(col.key ?? col.dataIndex))) {
                return col;
            }

            return {
                ...col,
                className: classNames("highlight-animation", isHighlight && "highlight-column"),
            };
        });
    }, [columns, columnNamesForHighlight, isHighlight]);
};

export default useHighlightColumns;
